import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../context/LanguageContext';
import {
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  Layers,
  Tag,
  Users,
  Circle as CircleIcon,
  Square as SquareIcon,
  RectangleHorizontal,
  Utensils,
  DoorOpen,
  Bath,
  Sprout,
  Box as BoxIcon,
  Receipt,
  Wine,
  ArrowUpDown,
  Columns,
  AlertTriangle,
  RotateCw,
  Maximize2,
  Minimize2,
  Grid3x3,
  Expand,
  X,
} from 'lucide-react';
import { confirm, alertDialog } from './ui/confirm-dialog';
import { mastersService } from '../services/mastersService';
import { floorPlanService } from '../services/floorPlanService';
import { FIXTURE_EMOJI, fixtureGlyphs } from '../lib/fixtureEmoji';

/* FloorPlanDesigner: a left rail of floors, sections and palette beside a canvas of drag-positioned items. */

const GRID = 12;

/* Each type keeps its lucide icon only as the fallback for a type the emoji
   map does not know; what the palette and the plan actually show is the
   emoji (🪴, 🪟, 🚪…) — see lib/fixtureEmoji.js, shared with every other
   screen that draws these plans. */
const FIXTURE_PALETTE = [
  { type: 'kitchen',   label: 'Kitchen',    icon: Utensils,             w: 160, h: 90,  color: '#fde68a', text: '#92400e' },
  { type: 'washroom',  label: 'Washroom',   icon: Bath,                 w: 100, h: 80,  color: '#bae6fd', text: '#075985' },
  { type: 'stairs',    label: 'Stairs',     icon: ArrowUpDown,          w: 80,  h: 120, color: '#e2e8f0', text: '#334155' },
  { type: 'plant',     label: 'Plant',      icon: Sprout,               w: 50,  h: 50,  color: '#bbf7d0', text: '#166534' },
  { type: 'door',      label: 'Door',       icon: DoorOpen,             w: 70,  h: 24,  color: '#ddd6fe', text: '#5b21b6' },
  { type: 'window',    label: 'Window',     icon: BoxIcon,              w: 80,  h: 16,  color: '#dbeafe', text: '#1d4ed8' },
  { type: 'column',    label: 'Column',     icon: Columns,              w: 40,  h: 40,  color: '#cbd5e1', text: '#1e293b' },
  { type: 'reception', label: 'Reception',  icon: Receipt,              w: 140, h: 60,  color: '#fbcfe8', text: '#9d174d' },
  { type: 'bar',       label: 'Bar Counter',icon: Wine,                 w: 180, h: 60,  color: '#fed7aa', text: '#9a3412' },
];

const TABLE_PALETTE = [
  { shape: 'rectangle', label: '2-seater', w: 80,  h: 60, capacity: 2 },
  { shape: 'rectangle', label: '4-seater', w: 110, h: 70, capacity: 4 },
  { shape: 'rectangle', label: '6-seater', w: 150, h: 80, capacity: 6 },
  { shape: 'round',     label: 'Round 4',  w: 100, h: 100, capacity: 4 },
  { shape: 'round',     label: 'Round 6',  w: 120, h: 120, capacity: 6 },
  { shape: 'square',    label: 'Square 4', w: 90,  h: 90, capacity: 4 },
];

const CHAIR_GAP = 28;
const ITEM_PADDING = 8;
const CARDINAL_SLOTS = ['top', 'right', 'bottom', 'left'];
const CARDINAL_SPACING = 88;

const tmpId = () => `tmp-${Math.random().toString(36).slice(2, 9)}`;

function snap(v) {
  return Math.round(v / GRID) * GRID;
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function symmetricOffset(index) {
  if (index <= 0) return 0;
  const step = Math.ceil(index / 2);
  return index % 2 === 1 ? step : -step;
}

function getCardinalMetrics(section, item) {
  const width = section.canvas_width || 1200;
  const height = section.canvas_height || 800;
  const centerX = width / 2;
  const centerY = height / 2;
  const laneDistance = Math.max(132, Math.min(width, height) * 0.2);
  return { width, height, centerX, centerY, laneDistance, itemWidth: item.width, itemHeight: item.height };
}

function getCardinalPlacement(section, item, index) {
  const { width, height, centerX, centerY, laneDistance } = getCardinalMetrics(section, item);
  const slot = CARDINAL_SLOTS[index % CARDINAL_SLOTS.length];
  const offset = symmetricOffset(Math.floor(index / CARDINAL_SLOTS.length)) * CARDINAL_SPACING;
  const raw =
    slot === 'top'
      ? { x: centerX + offset - item.width / 2, y: centerY - laneDistance - item.height / 2 }
      : slot === 'right'
        ? { x: centerX + laneDistance - item.width / 2, y: centerY + offset - item.height / 2 }
        : slot === 'bottom'
          ? { x: centerX + offset - item.width / 2, y: centerY + laneDistance - item.height / 2 }
          : { x: centerX - laneDistance - item.width / 2, y: centerY + offset - item.height / 2 };
  return {
    placement_slot: slot,
    x: snap(clamp(raw.x, ITEM_PADDING, Math.max(ITEM_PADDING, width - item.width - ITEM_PADDING))),
    y: snap(clamp(raw.y, ITEM_PADDING, Math.max(ITEM_PADDING, height - item.height - ITEM_PADDING))),
  };
}

export default function FloorPlanDesigner({ restaurantId, locationId, locationName }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);

  const [floors, setFloors] = useState([]);           // [{ _key, id?, name, floor_number, sections: [...] }]
  const [sectionTypes, setSectionTypes] = useState([]);
  const [activeFloorKey, setActiveFloorKey] = useState(null);
  const [activeSectionKey, setActiveSectionKey] = useState(null);
  const [selected, setSelected] = useState(null);    // { kind: 'table'|'fixture', key }
  const [fullscreen, setFullscreen] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const canvasAreaRef = useRef(null);

  // Load initial data
  useEffect(() => {
    if (!restaurantId || !locationId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      floorPlanService.get(restaurantId, locationId).catch(() => ({ floors: [] })),
      mastersService.list('sectionTypes'),
    ])
      .then(([plan, types]) => {
        if (cancelled) return;
        setSectionTypes(types || []);
        const hydrated = hydratePlan(plan?.floors || []);
        if (!hydrated.length) {
          const f = newFloor(0);
          hydrated.push(f);
        }
        setFloors(hydrated);
        setActiveFloorKey(hydrated[0]._key);
        setActiveSectionKey(hydrated[0].sections[0]?._key || null);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [restaurantId, locationId]);

  const activeFloor = useMemo(
    () => floors.find((f) => f._key === activeFloorKey),
    [floors, activeFloorKey],
  );
  const activeSection = useMemo(
    () => activeFloor?.sections.find((s) => s._key === activeSectionKey),
    [activeFloor, activeSectionKey],
  );

  /* -------------------------- floor ops -------------------------- */

  const addFloor = () => {
    const nextNumber = floors.length ? Math.max(...floors.map((f) => f.floor_number)) + 1 : 0;
    const f = newFloor(nextNumber);
    setFloors((prev) => [...prev, f]);
    setActiveFloorKey(f._key);
    setActiveSectionKey(f.sections[0]._key);
  };

  const deleteFloor = async (key) => {
    if (floors.length <= 1) {
      await alertDialog({ title: 'Not allowed', description: `At least one floor is required.`, tone: 'destructive' });
      return;
    }
    if (!(await confirm({ description: `Delete this floor and all its sections?`, confirmText: 'Delete', tone: 'destructive' }))) return;
    const next = floors.filter((f) => f._key !== key);
    setFloors(next);
    if (activeFloorKey === key) {
      setActiveFloorKey(next[0]._key);
      setActiveSectionKey(next[0].sections[0]?._key || null);
    }
  };

  const renameFloor = (key, name) => {
    setFloors((prev) => prev.map((f) => (f._key === key ? { ...f, name } : f)));
  };

  /* -------------------------- section ops ------------------------ */

  const addSection = () => {
    if (!activeFloor) return;
    // Generate a unique default name within this floor
    const base = `Section`;
    let idx = activeFloor.sections.length + 1;
    let name = idx === 1 ? 'Main Hall' : `${base} ${idx}`;
    const names = new Set(activeFloor.sections.map((s) => s.name));
    while (names.has(name)) {
      idx += 1;
      name = `${base} ${idx}`;
    }
    const s = { ...newSection(), name };
    setFloors((prev) =>
      prev.map((f) => (f._key === activeFloorKey ? { ...f, sections: [...f.sections, s] } : f)),
    );
    setActiveSectionKey(s._key);
  };

  const deleteSection = async (key) => {
    if (!activeFloor) return;
    if (activeFloor.sections.length <= 1) {
      await alertDialog({ title: 'Not allowed', description: `Each floor must have at least one section.`, tone: 'destructive' });
      return;
    }
    if (!(await confirm({ description: `Delete this section?`, confirmText: 'Delete', tone: 'destructive' }))) return;
    const newSections = activeFloor.sections.filter((s) => s._key !== key);
    setFloors((prev) =>
      prev.map((f) => (f._key === activeFloorKey ? { ...f, sections: newSections } : f)),
    );
    if (activeSectionKey === key) setActiveSectionKey(newSections[0]._key);
  };

  const updateSection = (patch) => {
    // If renaming, ensure uniqueness within the floor
    if (patch && typeof patch.name === 'string') {
      const newName = patch.name.trim();
      if (newName) {
        const dup = (activeFloor?.sections || []).some((s) => s._key !== activeSectionKey && s.name === newName);
        if (dup) {
          alertDialog({ title: 'Not allowed', description: `Section names must be unique within a floor. Pick a different name.`, tone: 'destructive' });
          return;
        }
      }
    }
    setFloors((prev) =>
      prev.map((f) =>
        f._key === activeFloorKey
          ? {
              ...f,
              sections: f.sections.map((s) => (s._key === activeSectionKey ? { ...s, ...patch } : s)),
            }
          : f,
      ),
    );
  };

  /* -------------------------- item ops --------------------------- */

  const mutateActiveSection = (mutator) => {
    setFloors((prev) =>
      prev.map((f) =>
        f._key === activeFloorKey
          ? { ...f, sections: f.sections.map((s) => (s._key === activeSectionKey ? mutator(s) : s)) }
          : f,
      ),
    );
  };

  const addFixture = (paletteItem) => {
    const activeFixtures = activeSection?.fixtures || [];
    const placement = getCardinalPlacement(activeSection, { width: paletteItem.w, height: paletteItem.h }, activeFixtures.length);
    const f = {
      _key: tmpId(),
      fixture_type: paletteItem.type,
      label: paletteItem.label,
      ...placement,
      width: paletteItem.w,
      height: paletteItem.h,
      rotation: 0,
      color: paletteItem.color,
      _text: paletteItem.text,
    };
    mutateActiveSection((s) => ({ ...s, fixtures: [...s.fixtures, f] }));
    setSelected({ kind: 'fixture', key: f._key });
  };

  const addTable = (paletteItem) => {
    const activeTables = activeSection?.tables || [];
    const code = nextTableCode(floors);
    const placement = getCardinalPlacement(activeSection, { width: paletteItem.w, height: paletteItem.h }, activeTables.length);
    const t = {
      _key: tmpId(),
      table_code: code,
      capacity: paletteItem.capacity,
      shape: paletteItem.shape,
      ...placement,
      width: paletteItem.w,
      height: paletteItem.h,
      rotation: 0,
      // Both booking channels may hand a new table out until someone says otherwise.
      reservation_allowed_yn: 'Y',
      website_booking_allowed_yn: 'Y',
    };
    mutateActiveSection((s) => ({ ...s, tables: [...s.tables, t] }));
    setSelected({ kind: 'table', key: t._key });
  };

  const updateItem = (kind, key, patch) => {
    mutateActiveSection((s) => {
      const list = kind === 'table' ? s.tables : s.fixtures;
      const next = list.map((it) => {
        if (it._key !== key) return it;
        // Free placement: patches (including resize) keep the item where it is.
        return { ...it, ...patch };
      });
      return kind === 'table' ? { ...s, tables: next } : { ...s, fixtures: next };
    });
  };

  const deleteItem = (kind, key) => {
    mutateActiveSection((s) => {
      const list = kind === 'table' ? s.tables : s.fixtures;
      const next = list.filter((it) => it._key !== key);
      return kind === 'table' ? { ...s, tables: next } : { ...s, fixtures: next };
    });
    setSelected(null);
  };

  const totalSeats = useMemo(() => {
    return floors.reduce(
      (n, f) => n + f.sections.reduce((m, s) => m + s.tables.reduce((c, t) => c + (t.capacity || 0), 0), 0),
      0,
    );
  }, [floors]);

  /* Tables across the whole plan: seats alone don't answer "how many tables have I laid out". */
  const totalTables = useMemo(
    () => floors.reduce((n, f) => n + f.sections.reduce((m, s) => m + s.tables.length, 0), 0),
    [floors],
  );

  const sectionSeats = useMemo(
    () => (activeSection?.tables || []).reduce((c, t) => c + (t.capacity || 0), 0),
    [activeSection],
  );

  /* -------------------------- view ops --------------------------- */

  const fitToView = () => {
    if (!canvasAreaRef.current || !activeSection) return;
    const rect = canvasAreaRef.current.getBoundingClientRect();
    // subtract the area's padding (p-6 = 24px each side)
    const w = Math.max(GRID * 20, snap(rect.width - 48));
    const h = Math.max(GRID * 20, snap(rect.height - 48));
    updateSection({ canvas_width: w, canvas_height: h });
  };

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Delete / Backspace removes the selected item, ignored while typing so backspacing text never nukes it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!selected) return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      e.preventDefault();
      deleteItem(selected.kind, selected.key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------------- save ------------------------------- */

  const save = async () => {
    if (!restaurantId || !locationId) return;
    setSaving(true);
    setError(null);
    setSaveOk(false);
    try {
      const payload = { floors: serializePlan(floors) };
      const fresh = await floorPlanService.save(restaurantId, locationId, payload);
      const hydrated = hydratePlan(fresh?.floors || []);
      setFloors(hydrated);
      // Try to keep selection on the same floor by name + section by name
      const fname = activeFloor?.name;
      const sname = activeSection?.name;
      const nf = hydrated.find((f) => f.name === fname) || hydrated[0];
      setActiveFloorKey(nf?._key || null);
      const ns = nf?.sections.find((s) => s.name === sname) || nf?.sections[0];
      setActiveSectionKey(ns?._key || null);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2200);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to save floor plan');
    } finally {
      setSaving(false);
    }
  };

  /* ============================ render =========================== */

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading floor plan…</div>;
  }

  return (
    <div
      className={
        fullscreen
          ? 'fixed inset-0 z-50 flex bg-slate-50'
          : 'h-full flex bg-slate-50'
      }
    >
      {/* LEFT RAIL */}
      <aside className="w-80 shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Floor Plan</div>
          <div className="text-sm font-semibold text-slate-900 truncate">{locationName || 'Location'}</div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
            <Users size={11} /> {totalTables} table(s) · {totalSeats} seat(s) · {floors.length} floor(s)
          </div>
        </div>

        {/* Floors */}
        <Section title="Floors" actionLabel="Add" onAction={addFloor}>
          <div className="space-y-1">
            {floors.map((f) => (
              <FloorRow
                key={f._key}
                floor={f}
                active={f._key === activeFloorKey}
                onSelect={() => {
                  setActiveFloorKey(f._key);
                  setActiveSectionKey(f.sections[0]?._key || null);
                  setSelected(null);
                }}
                onRename={(name) => renameFloor(f._key, name)}
                onDelete={() => deleteFloor(f._key)}
              />
            ))}
          </div>
        </Section>

        {/* Sections of active floor */}
        {activeFloor && (
          <Section title="Sections" actionLabel="Add" onAction={addSection}>
            <div className="space-y-1">
              {activeFloor.sections.map((s) => (
                <SectionRow
                  key={s._key}
                  section={s}
                  active={s._key === activeSectionKey}
                  onSelect={() => { setActiveSectionKey(s._key); setSelected(null); }}
                  onDelete={() => deleteSection(s._key)}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Section config */}
        {activeSection && (
          <Section title="Section Config">
            <div className="space-y-3 text-sm">
              <div>
                <Label>Name</Label>
                <Input value={activeSection.name} onChange={(e) => updateSection({ name: e.target.value })} />
              </div>
              <div>
                <Label className="inline-flex items-center gap-1"><Tag size={11} /> Type</Label>
                <Select
                  value={activeSection.section_type_id ?? ''}
                  onChange={(e) => updateSection({ section_type_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">— Select type —</option>
                  {sectionTypes.filter((t) => t.is_active !== false).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Canvas W</Label>
                  <Input
                    type="number"
                    value={activeSection.canvas_width}
                    onChange={(e) => updateSection({ canvas_width: Number(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Canvas H</Label>
                  <Input
                    type="number"
                    value={activeSection.canvas_height}
                    onChange={(e) => updateSection({ canvas_height: Number(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="text-[11px] text-slate-500">
                {activeSection.tables.length} table(s) · {sectionSeats} seat(s) · {activeSection.fixtures.length} fixture(s)
              </div>
            </div>
          </Section>
        )}

        {/* Fixture palette */}
        {activeSection && (
          <Section title="Architecture (drag onto plan)">
            <div className="grid grid-cols-2 gap-2">
              {FIXTURE_PALETTE.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.type}
                    type="button"
                    onClick={() => addFixture(p)}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    style={{ borderLeft: `3px solid ${p.color}` }}
                  >
                    {FIXTURE_EMOJI[p.type]
                      ? <span className="text-sm leading-none" aria-hidden="true">{FIXTURE_EMOJI[p.type]}</span>
                      : <Icon size={14} style={{ color: p.text }} />}
                    <span className="truncate">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </Section>
        )}

        {/* Table palette */}
        {activeSection && (
          <Section title="Tables (drag onto plan)">
            <div className="grid grid-cols-2 gap-2">
              {TABLE_PALETTE.map((p) => {
                const Icon = p.shape === 'round' ? CircleIcon : p.shape === 'square' ? SquareIcon : RectangleHorizontal;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => addTable(p)}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                  >
                    <Icon size={14} className="text-amber-700" />
                    <span className="truncate">{p.label}</span>
                    <span className="ml-auto text-[10px] text-slate-500">×{p.capacity}</span>
                  </button>
                );
              })}
            </div>
          </Section>
        )}

        {/* Selected item editor */}
        {selected && activeSection && (
          <SelectedEditor
            kind={selected.kind}
            item={
              selected.kind === 'table'
                ? activeSection.tables.find((t) => t._key === selected.key)
                : activeSection.fixtures.find((f) => f._key === selected.key)
            }
            section={activeSection}
            onChange={(patch) => updateItem(selected.kind, selected.key, patch)}
            onDelete={() => deleteItem(selected.kind, selected.key)}
          />
        )}
      </aside>

      {/* CANVAS */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 text-sm">
            <Layers size={14} className="text-blue-600" />
            <span className="font-medium text-slate-800">{activeFloor?.name || 'Floor'}</span>
            {activeSection && (
              <>
                <span className="text-slate-400">/</span>
                <span className="text-slate-800">{activeSection.name}</span>
                {activeSection.section_type_id && (
                  <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-[10px] text-slate-600">
                    {sectionTypes.find((t) => t.id === activeSection.section_type_id)?.name || 'Type'}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {error && (
              <span className="inline-flex items-center gap-1 text-xs text-rose-600">
                <AlertTriangle size={12} /> {error}
              </span>
            )}
            {saveOk && (
              <span className="text-xs text-emerald-600">Saved ✓</span>
            )}
            <button
              type="button"
              onClick={() => setShowGrid((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                showGrid
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title="Toggle grid"
            >
              <Grid3x3 size={13} /> Grid
            </button>
            <button
              type="button"
              onClick={fitToView}
              disabled={!activeSection}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              title="Resize canvas to fill viewport"
            >
              <Expand size={13} /> Fit
            </button>
            <button
              type="button"
              onClick={() => setFullscreen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              title={fullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
            >
              {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              {fullscreen ? 'Exit' : 'Fullscreen'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? 'Saving…' : 'Save Plan'}
            </button>
          </div>
        </div>

        <div
          ref={canvasAreaRef}
          className="flex-1 overflow-auto p-6"
          style={{
            background: 'linear-gradient(180deg, #f6f2e8 0%, #f2ebdd 100%)',
          }}
        >
          {activeSection ? (
            <Canvas
              section={activeSection}
              selected={selected}
              showGrid={showGrid}
              onSelect={(s) => setSelected(s)}
              onUpdateItem={updateItem}
              onDeleteItem={deleteItem}
              onClearSelection={() => setSelected(null)}
            />
          ) : (
            <div className="text-sm text-slate-500">Add a section to begin.</div>
          )}
        </div>
      </main>
    </div>
  );
}

/* Canvas. */

function Canvas({ section, selected, showGrid, onSelect, onUpdateItem, onDeleteItem, onClearSelection }) {
  const ref = useRef(null);

  // Clean grid: subtle, low-contrast lines that keep the plan readable.
  const gridStyle = showGrid
    ? {
        backgroundImage: [
          'linear-gradient(to right, rgba(15,23,42,0.03) 1px, transparent 1px)',
          'linear-gradient(to bottom, rgba(15,23,42,0.03) 1px, transparent 1px)',
          'linear-gradient(to right, rgba(15,23,42,0.015) 1px, transparent 1px)',
          'linear-gradient(to bottom, rgba(15,23,42,0.015) 1px, transparent 1px)',
        ].join(','),
        backgroundSize: `${GRID * 5}px ${GRID * 5}px, ${GRID * 5}px ${GRID * 5}px, ${GRID}px ${GRID}px, ${GRID}px ${GRID}px`,
      }
    : null;

  return (
    <div
      ref={ref}
      onMouseDown={(e) => {
        if (e.target === ref.current) onClearSelection();
      }}
      className="relative rounded-lg border border-slate-200 shadow-inner mx-auto"
      style={{
        width: section.canvas_width,
        height: section.canvas_height,
        // Warm off-white, so tables and fixtures are what stand out.
        background: '#fffdf8',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -10px 20px rgba(0,0,0,0.025)',
        ...gridStyle,
      }}
    >
      {section.fixtures.map((f) => (
        <FixtureItem
          key={f._key}
          item={f}
          section={section}
          selected={selected?.kind === 'fixture' && selected.key === f._key}
          onSelect={() => onSelect({ kind: 'fixture', key: f._key })}
          onUpdate={(patch) => onUpdateItem('fixture', f._key, patch)}
          onDelete={() => onDeleteItem('fixture', f._key)}
        />
      ))}
      {section.tables.map((t) => (
        <TableItem
          key={t._key}
          item={t}
          section={section}
          selected={selected?.kind === 'table' && selected.key === t._key}
          onSelect={() => onSelect({ kind: 'table', key: t._key })}
          onUpdate={(patch) => onUpdateItem('table', t._key, patch)}
          onDelete={() => onDeleteItem('table', t._key)}
        />
      ))}
    </div>
  );
}

/* Fixture item. */

function FixtureItem({ item, section, selected, onSelect, onUpdate, onDelete }) {
  const paletteEntry = FIXTURE_PALETTE.find((p) => p.type === item.fixture_type);
  const Icon = paletteEntry?.icon || BoxIcon;
  const color = item.color || paletteEntry?.color || '#e2e8f0';
  const text = paletteEntry?.text || '#334155';
  /* The emoji face: one glyph for a boxy fixture, a RUN of them for a strip —
     🪟🪟🪟 lining a wall reads as panes, not one lost glyph. Laid along the
     strip's own axis and sized to its thin side. */
  const glyphs = fixtureGlyphs(item.fixture_type, item.width, item.height);
  const vertical = (item.height || 0) > (item.width || 0);
  const thinSide = Math.max(1, Math.min(item.width || 0, item.height || 0));
  const glyphSize = glyphs.length > 1
    ? Math.min(20, Math.max(10, Math.round(thinSide * 0.75)))
    : Math.min(26, Math.max(12, Math.round(thinSide * 0.5)));

  const onMouseDown = (e) => {
    e.stopPropagation();
    onSelect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { x: item.x, y: item.y };
    const { width, height } = getCardinalMetrics(section, item);
    const onMove = (ev) => {
      const deltaX = ev.clientX - startX;
      const deltaY = ev.clientY - startY;
      const minX = ITEM_PADDING;
      const maxX = Math.max(ITEM_PADDING, width - item.width - ITEM_PADDING);
      const minY = ITEM_PADDING;
      const maxY = Math.max(ITEM_PADDING, height - item.height - ITEM_PADDING);
      // Free 2D drag — move anywhere on the canvas (clamped to its bounds).
      onUpdate({
        x: snap(clamp(startPos.x + deltaX, minX, maxX)),
        y: snap(clamp(startPos.y + deltaY, minY, maxY)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  /* The corner grip's resize: length and breadth by hand, snapped to the
     grid and held inside the room — so a window is STRETCHED along a whole
     wall instead of retyped as numbers (the W/H boxes stay for exact ones). */
  const onResizeDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = item.width;
    const startH = item.height;
    const { width, height } = getCardinalMetrics(section, item);
    const onMove = (ev) => onUpdate({
      width: snap(clamp(startW + (ev.clientX - startX), GRID,
        Math.max(GRID, width - item.x - ITEM_PADDING))),
      height: snap(clamp(startH + (ev.clientY - startY), GRID,
        Math.max(GRID, height - item.y - ITEM_PADDING))),
    });
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      className={`group absolute flex flex-col items-center justify-center rounded-md cursor-move select-none transition-shadow ${
        selected ? 'ring-2 ring-blue-500 shadow-lg' : 'shadow-sm'
      }`}
      style={{
        left: item.x,
        top: item.y,
        width: item.width,
        height: item.height,
        background: color,
        color: text,
        transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
      }}
      title={item.label || item.fixture_type}
    >
      {glyphs.length > 0 ? (
        <div
          aria-hidden="true"
          className={`flex ${vertical ? 'flex-col' : 'flex-row'} items-center justify-center overflow-hidden leading-none`}
          style={{ fontSize: glyphSize, maxWidth: '100%', maxHeight: '100%' }}
        >
          {glyphs.map((g, i) => <span key={i}>{g}</span>)}
        </div>
      ) : (
        /* A type the emoji map does not know keeps its lucide icon — sized
           near its own height on short fixtures so it never becomes a speck. */
        <Icon size={Math.min(20, item.height >= 40 ? item.height * 0.4 : Math.max(12, item.height - 6))} />
      )}
      {item.height > 36 && Math.min(item.width, item.height) > 30 && (
        <div className="text-[10px] font-medium mt-0.5 truncate px-1 max-w-full">
          {item.label || item.fixture_type}
        </div>
      )}
      {/* Resize grip, on the thing itself while selected. */}
      {selected && (
        <span
          onMouseDown={onResizeDown}
          role="button"
          tabIndex={-1}
          aria-label={`Resize ${item.label || item.fixture_type}`}
          title="Drag to resize — length and breadth"
          className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-nwse-resize rounded-sm border-2 border-white bg-blue-500 shadow"
        />
      )}
      {/* The way OFF the plan, on the thing itself: hover and press the ✕ —
          the same gesture the buffet planner's stations use. Mousedown is
          stopped so pressing it never starts a drag. */}
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full border border-red-300 bg-white text-red-600 shadow group-hover:flex"
        aria-label={`Remove ${item.label || item.fixture_type} from the plan`}
        title="Remove from the plan"
      >
        <X size={11} />
      </button>
    </div>
  );
}

/* Table item (with chairs around it). */

function TableItem({ item, section, selected, onSelect, onUpdate, onDelete }) {
  const onMouseDown = (e) => {
    e.stopPropagation();
    onSelect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { x: item.x, y: item.y };
    const { width, height } = getCardinalMetrics(section, item);
    const onMove = (ev) => {
      const deltaX = ev.clientX - startX;
      const deltaY = ev.clientY - startY;
      const minX = ITEM_PADDING;
      const maxX = Math.max(ITEM_PADDING, width - item.width - ITEM_PADDING);
      const minY = ITEM_PADDING;
      const maxY = Math.max(ITEM_PADDING, height - item.height - ITEM_PADDING);
      // Free 2D drag — move anywhere on the canvas (clamped to its bounds).
      onUpdate({
        x: snap(clamp(startPos.x + deltaX, minX, maxX)),
        y: snap(clamp(startPos.y + deltaY, minY, maxY)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const isRound = item.shape === 'round';
  const tableBg =
    'linear-gradient(135deg, #b45309 0%, #92400e 50%, #78350f 100%)'; // warm wood
  const chairBg = '#cbd5e1';

  return (
    <div
      onMouseDown={onMouseDown}
      className={`absolute select-none cursor-move group ${selected ? 'z-20' : 'z-10'}`}
      style={{
        left: item.x - CHAIR_GAP,
        top: item.y - CHAIR_GAP,
        width: item.width + CHAIR_GAP * 2,
        height: item.height + CHAIR_GAP * 2,
        transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
      }}
    >
      {/* Chairs */}
      {chairsAround(item).map((c, i) => (
        <div
          key={i}
          className="absolute rounded-md"
          style={{
            left: c.left,
            top: c.top,
            width: 16,
            height: 16,
            background: chairBg,
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      ))}
      {/* Table */}
      <div
        className={`absolute flex items-center justify-center text-white font-semibold ${
          selected ? 'ring-2 ring-blue-500' : ''
        }`}
        style={{
          left: CHAIR_GAP,
          top: CHAIR_GAP,
          width: item.width,
          height: item.height,
          background: tableBg,
          borderRadius: isRound ? '50%' : 8,
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          fontSize: Math.min(14, item.height * 0.25),
        }}
      >
        {item.table_code || `×${item.capacity}`}
      </div>
      {/* The way OFF the plan, on the table itself: hover and press the ✕.
          Anchored to the table surface (inside the chair margin) so it sits on
          the wood, not floating over a chair. Mousedown is stopped so pressing
          it never starts a drag. */}
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute z-30 hidden h-5 w-5 items-center justify-center rounded-full border border-red-300 bg-white text-red-600 shadow group-hover:flex"
        style={{ left: CHAIR_GAP + item.width - 10, top: CHAIR_GAP - 10 }}
        aria-label={`Remove table ${item.table_code || ''} from the plan`}
        title="Remove this table from the plan"
      >
        <X size={11} />
      </button>
    </div>
  );
}

function chairsAround(t) {
  const n = Math.max(0, t.capacity || 0);
  if (n === 0) return [];
  const cx = CHAIR_GAP + t.width / 2;
  const cy = CHAIR_GAP + t.height / 2;
  if (t.shape === 'round') {
    const r = Math.max(t.width, t.height) / 2 + 14;
    return Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return { left: cx + r * Math.cos(a) - 8, top: cy + r * Math.sin(a) - 8 };
    });
  }
  // rectangle/square: round-robin distribute across the 4 sides
  const placements = [];
  const edges = ['top', 'right', 'bottom', 'left'];
  for (let i = 0; i < n; i++) {
    placements.push({ edge: edges[i % 4], idx: Math.floor(i / 4) });
  }
  const perEdge = { top: 0, right: 0, bottom: 0, left: 0 };
  placements.forEach((p) => { perEdge[p.edge] += 1; });
  const seen = { top: 0, right: 0, bottom: 0, left: 0 };
  return placements.map((p) => {
    const slot = seen[p.edge]++;
    const total = perEdge[p.edge];
    const fraction = (slot + 1) / (total + 1);
    if (p.edge === 'top') return { left: CHAIR_GAP + t.width * fraction - 8, top: CHAIR_GAP - 22 };
    if (p.edge === 'bottom') return { left: CHAIR_GAP + t.width * fraction - 8, top: CHAIR_GAP + t.height + 6 };
    if (p.edge === 'left') return { left: CHAIR_GAP - 22, top: CHAIR_GAP + t.height * fraction - 8 };
    return { left: CHAIR_GAP + t.width + 6, top: CHAIR_GAP + t.height * fraction - 8 };
  });
}

/* Selected-item editor (right sidebar block). */

function SelectedEditor({ kind, item, section, onChange, onDelete }) {
  const { t } = useT();
  if (!item) return null;
  return (
    <Section title={kind === 'table' ? 'Edit Table' : 'Edit Fixture'}>
      <div className="space-y-2 text-sm">
        {kind === 'table' ? (
          <>
            <Row>
              <Label>Code</Label>
              <Input value={item.table_code || ''} onChange={(e) => onChange({ table_code: e.target.value })} />
            </Row>
            <Row>
              <Label>Capacity</Label>
              {/* Text, not number: a controlled number input keeps a typed
                  leading zero ("03") while focused; the cleaned string here
                  re-renders as the number it means the moment it is typed. */}
              <Input
                type="text"
                inputMode="numeric"
                value={item.capacity ?? 0}
                onChange={(e) => onChange({
                  capacity: Math.max(0, Number(e.target.value.replace(/\D+/g, '')) || 0),
                })}
              />
            </Row>
            <Row>
              <Label>Shape</Label>
              <Select value={item.shape} onChange={(e) => onChange({ shape: e.target.value })}>
                <option value="rectangle">Rectangle</option>
                <option value="round">Round</option>
                <option value="square">Square</option>
              </Select>
            </Row>
            {/* Which booking channels may hand this table out. Two switches,
                not one: a table a manager will give a phone caller is not
                necessarily one the website should sell unattended. */}
            <Row>
              <Label>{t('tables.designer.reservations')}</Label>
              <Select
                value={item.reservation_allowed_yn === 'N' ? 'N' : 'Y'}
                onChange={(e) => onChange({ reservation_allowed_yn: e.target.value })}
              >
                <option value="Y">{t('tables.designer.allowed')}</option>
                <option value="N">{t('tables.designer.notAllowed')}</option>
              </Select>
            </Row>
            <Row>
              <Label>{t('tables.designer.website')}</Label>
              <Select
                value={item.website_booking_allowed_yn === 'N' ? 'N' : 'Y'}
                onChange={(e) => onChange({ website_booking_allowed_yn: e.target.value })}
              >
                <option value="Y">{t('tables.designer.allowed')}</option>
                <option value="N">{t('tables.designer.notAllowed')}</option>
              </Select>
            </Row>
            <div className="text-[11px] text-slate-500">{t('tables.designer.channelsHint')}</div>
          </>
        ) : (
          <Row>
            <Label>Label</Label>
            <Input value={item.label || ''} onChange={(e) => onChange({ label: e.target.value })} />
          </Row>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Row><Label>W</Label><Input type="number" value={item.width} onChange={(e) => onChange({ width: Number(e.target.value) || 1 })} /></Row>
          <Row><Label>H</Label><Input type="number" value={item.height} onChange={(e) => onChange({ height: Number(e.target.value) || 1 })} /></Row>
        </div>
        {kind === 'fixture' && (() => {
          /* One tap runs the fixture the LENGTH of a wall — a window through
             the whole left side is the everyday case. The thin dimension is
             kept (or capped) so a stretched window stays a strip, not a slab. */
          const cw = section.canvas_width || 1200;
          const chh = section.canvas_height || 800;
          const thin = Math.max(12, Math.min(item.width, item.height, 40));
          const wall = (patch) => onChange({ rotation: 0, ...patch });
          return (
            <div>
              <div className="mb-1 text-[11px] text-slate-500">Run it along a wall, end to end:</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                  onClick={() => wall({ x: ITEM_PADDING, y: ITEM_PADDING, width: thin, height: chh - ITEM_PADDING * 2 })}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs hover:bg-slate-50">
                  ⬅ Left wall
                </button>
                <button type="button"
                  onClick={() => wall({ x: cw - thin - ITEM_PADDING, y: ITEM_PADDING, width: thin, height: chh - ITEM_PADDING * 2 })}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs hover:bg-slate-50">
                  Right wall ➡
                </button>
                <button type="button"
                  onClick={() => wall({ x: ITEM_PADDING, y: ITEM_PADDING, width: cw - ITEM_PADDING * 2, height: thin })}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs hover:bg-slate-50">
                  ⬆ Top wall
                </button>
                <button type="button"
                  onClick={() => wall({ x: ITEM_PADDING, y: chh - thin - ITEM_PADDING, width: cw - ITEM_PADDING * 2, height: thin })}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs hover:bg-slate-50">
                  ⬇ Bottom wall
                </button>
              </div>
            </div>
          );
        })()}
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          Drag the item anywhere on the plan.{' '}
          {kind === 'fixture'
            ? 'Drag the blue corner grip to resize it — stretch a window the length of a wall — or rotate below.'
            : 'Set W and H above for its size, or rotate below.'}{' '}
          Press <kbd className="px-1 py-0.5 rounded border border-slate-300 bg-white text-[10px]">Delete</kbd> to remove it.
        </div>
        <button
          type="button"
          onClick={() => onChange({ rotation: ((item.rotation || 0) + 90) % 360 })}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          <RotateCw size={12} /> Rotate 90°
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-100"
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </Section>
  );
}

/* UI primitives and rows. */

function Section({ title, actionLabel, onAction, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</span>
        <span className="flex items-center gap-2">
          {actionLabel && open && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onAction?.(); }}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800"
            >
              <Plus size={11} /> {actionLabel}
            </span>
          )}
          {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function FloorRow({ floor, active, onSelect, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  return (
    <div
      onClick={onSelect}
      className={`group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer ${
        active ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
      }`}
    >
      <Layers size={13} className={active ? 'text-blue-600' : 'text-slate-400'} />
      {editing ? (
        <input
          autoFocus
          value={floor.name}
          onChange={(e) => onRename(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => { if (e.key === 'Enter') setEditing(false); }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 bg-transparent border-b border-blue-400 text-sm outline-none"
        />
      ) : (
        <span
          className="flex-1 text-sm text-slate-800 truncate"
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
          title="Double-click to rename"
        >
          {floor.name}
        </span>
      )}
      {/* Sections and tables in the same shorthand the section rows use, since a section count isn't a layout. */}
      <span
        className="text-[10px] text-slate-400 whitespace-nowrap"
        title={`${floor.sections.length} section(s), `
          + `${floor.sections.reduce((n, s) => n + s.tables.length, 0)} table(s)`}
      >
        {floor.sections.length} sec · {floor.sections.reduce((n, s) => n + s.tables.length, 0)}T
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100"
        title="Delete floor"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function SectionRow({ section, active, onSelect, onDelete }) {
  return (
    <div
      onClick={onSelect}
      className={`group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer ${
        active ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50 border border-transparent'
      }`}
    >
      <span className="inline-flex items-center justify-center h-5 w-5 rounded-sm border border-slate-200 bg-white text-[10px]">
        <Tag size={12} />
      </span>
      <span className="flex-1 text-sm text-slate-800 truncate">{section.name}</span>
      <span className="text-[10px] text-slate-400">{section.tables.length}T</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100"
        title="Delete section"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function Label({ children, className = '' }) {
  return <div className={`text-[11px] font-medium text-slate-600 mb-0.5 ${className}`}>{children}</div>;
}
function Input(props) {
  return (
    <input
      {...props}
      className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm leading-tight text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
    />
  );
}
function Select({ children, ...rest }) {
  return (
    <div className="relative">
      {/* A native select needs more height than a text input, or the selected label clips. */}
      <select
        {...rest}
        className="h-8 w-full appearance-none rounded-md border border-slate-300 bg-white pl-2 pr-7 py-1.5 text-sm leading-tight text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
      >
        {children}
      </select>
      <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}
function Row({ children }) {
  return <div>{children}</div>;
}

/* Helpers: hydrate / serialize / new entity factories. */

function newFloor(floor_number = 0) {
  return {
    _key: tmpId(),
    id: null,
    name: floor_number === 0 ? 'Ground Floor' : `Floor ${floor_number}`,
    floor_number,
    sections: [newSection(0)],
  };
}

function newSection(idx = 0) {
  return {
    _key: tmpId(),
    id: null,
    name: idx === 0 ? 'Main Hall' : `Section ${idx + 1}`,
    section_type_id: null,
    canvas_width: 1200,
    canvas_height: 800,
    tables: [],
    fixtures: [],
  };
}

function hydratePlan(apiFloors) {
  return apiFloors.map((f) => ({
    _key: `f-${f.id}`,
    id: f.id,
    name: f.name,
    floor_number: f.floor_number,
    sections: (f.sections || []).map((s) => ({
      _key: `s-${s.id}`,
      id: s.id,
      name: s.name,
      section_type_id: s.section_type_id || null,
      canvas_width: s.canvas_width || 1200,
      canvas_height: s.canvas_height || 800,
      tables: (s.tables || []).map((t) => ({
        _key: `t-${t.id}`,
        id: t.id,
        table_code: t.table_code,
        capacity: t.capacity,
        shape: t.shape || 'rectangle',
        x: t.x ?? 100,
        y: t.y ?? 100,
        width: t.width ?? 100,
        height: t.height ?? 70,
        rotation: t.rotation || 0,
        reservation_allowed_yn: t.reservation_allowed_yn === 'N' ? 'N' : 'Y',
        website_booking_allowed_yn: t.website_booking_allowed_yn === 'N' ? 'N' : 'Y',
        placement_slot: CARDINAL_SLOTS.includes(t.placement_slot) ? t.placement_slot : 'top',
      })),
      fixtures: (s.fixtures || []).map((fx) => ({
        _key: `x-${fx.id}`,
        id: fx.id,
        fixture_type: fx.fixture_type,
        label: fx.label || '',
        x: fx.x ?? 100,
        y: fx.y ?? 100,
        width: fx.width ?? 100,
        height: fx.height ?? 80,
        rotation: fx.rotation || 0,
        color: fx.color || '',
        placement_slot: CARDINAL_SLOTS.includes(fx.placement_slot) ? fx.placement_slot : 'top',
      })),
    })),
  }));
}

function serializePlan(floors) {
  return floors.map((f) => ({
    id: f.id || undefined,
    name: f.name,
    floor_number: f.floor_number,
    sections: f.sections.map((s) => ({
      id: s.id || undefined,
      name: s.name,
      section_type_id: s.section_type_id || null,
      canvas_width: s.canvas_width,
      canvas_height: s.canvas_height,
      tables: s.tables.map((t) => ({
        id: t.id || undefined,
        table_code: t.table_code,
        capacity: t.capacity,
        shape: t.shape,
        x: t.x,
        y: t.y,
        width: t.width,
        height: t.height,
        rotation: t.rotation,
        reservation_allowed_yn: t.reservation_allowed_yn === 'N' ? 'N' : 'Y',
        website_booking_allowed_yn: t.website_booking_allowed_yn === 'N' ? 'N' : 'Y',
        placement_slot: CARDINAL_SLOTS.includes(t.placement_slot) ? t.placement_slot : 'top',
      })),
      fixtures: s.fixtures.map((fx) => ({
        id: fx.id || undefined,
        fixture_type: fx.fixture_type,
        label: fx.label,
        x: fx.x,
        y: fx.y,
        width: fx.width,
        height: fx.height,
        rotation: fx.rotation,
        color: fx.color,
        placement_slot: CARDINAL_SLOTS.includes(fx.placement_slot) ? fx.placement_slot : 'top',
      })),
    })),
  }));
}

function nextTableCode(floors) {
  let max = 0;
  for (const f of floors) {
    for (const s of f.sections) {
      for (const t of s.tables) {
        const m = (t.table_code || '').match(/^T(\d+)$/);
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
    }
  }
  return `T${max + 1}`;
}
